import { useState } from 'react';

import { ArrowButton, Button, Checkbox, DatePicker, Modal, TextField, Typography } from "@king-ict/eupisi-ui/components";
import { useValidation } from '@king-ict/eupisi-ui/hooks';
import { nameof } from "@king-ict/eupisi-ui/utils";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router";

import { createProgramType, editProgramType } from "../../api/nisooApi";
import { useInput } from "../../hooks";
import { CodeBookValidationScheme, QualificationType } from '../../models/CodeBook';
import { ProgramTypeCodeBook } from '../../models/Program/ProgramTypeCodeBook';
import { useGetQualificationTypesQuery } from '../../queries/codeBookQuery';
import { codebookRouteProgramType } from '../../routes/routes';
import styles from "./RequestCodeBook.module.scss";

export default function CodeBookProgramTypeEdit(): JSX.Element {
  const history = useHistory();
  const location = useLocation();
  const { handleChange } = useInput();
  const { t } = useTranslation();
  const { data: qualificationTypes } = useGetQualificationTypesQuery();

  const getProgramInitial = () => {
    if ((location.state as any).programType) {
      return (location.state as any).programType as ProgramTypeCodeBook;
    }
    return { id: 0, name: "", qualificationMask: 0, canDelete: true };
  }

  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [programType, setProgramType] = useState<ProgramTypeCodeBook>(getProgramInitial());

  const [errors, doValidations] = useValidation({
    data: programType,
    validationSchema: CodeBookValidationScheme,
  });

  const saveChanges = () => {
    var isValid = doValidations();
    if (isValid) {
      setOpenConfirmModal(true);
    }
  }

  const handleCancelConfirmModal = async () => {
    setOpenConfirmModal(false);
  }

  const handleConfirmedConfirmModal = async () => {
    setOpenConfirmModal(false);
    if (programType.dateTo) {
      let date = new Date(programType.dateTo);
      programType.dateTo = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    }

    if (programType.id) {
      await editProgramType(programType.id, programType);
      navigateToDetails();
    }
    else {
      await createProgramType(programType);
      navigateToDetails();
    }
  }

  const cancel = () => {
    if (programType.id) {
      navigateToDetails()
    } else {
      navigateToList();
    }
  }

  const navigateToDetails = () => {
    history.push(
      codebookRouteProgramType(),
      {
        page: "details",
        programType: programType
      }
    );
  }

  const navigateToList = () => {
    history.push(
      codebookRouteProgramType()
    );
  }

  const handleQualificationMaskChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    var value = parseInt(e.target.value);
    var checked = e.target.checked;
    let newQualificationMask = programType.qualificationMask;

    if (checked) {
      newQualificationMask |= value;
    }
    else {
      newQualificationMask ^= value;
    }

    setProgramType((prev: ProgramTypeCodeBook) => ({
      ...prev,
      qualificationMask: newQualificationMask
    }));
  };

  const getQualificationTypeName = (id: number) => {
    let qualificationType = qualificationTypes?.find(qt => qt.id === id);

    return qualificationType?.name!;
  }

  return (
    <>
      <Row>
        <Col md={12}>
          <ArrowButton
            backwards
            ariaLabel=""
            onClick={navigateToList}
          />
          <span className={styles.backButtonCaption}>
            {t("codeBook.navigateToCodeBook")}
          </span>

        </Col>
      </Row>
      <Row className={styles.title}>
        <Col md={6}>
          <h3>{t("codeBook.programType")}</h3>
        </Col>
        <Col md={6} className="d-flex justify-content-end align-items-right">
          <Button
            outline
            onClick={() => cancel()}
            className={styles.actionButton}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={() => saveChanges()}
            className={styles.actionButton}>
            {t("common.save")}
          </Button>
        </Col>
      </Row>
      <Row>
        <Col md={8} className="mt-5">
          <TextField
            name={nameof<typeof programType>("name")}
            label={t("common.name")}
            value={programType.name!}
            maxLength={50}
            onChange={handleChange(setProgramType)}
            readonly={true}
            errorText={errors?.name}
          />
        </Col>
      </Row>
      <Row>
        <Col md={12} className="mt-5">
          <Checkbox
            label={getQualificationTypeName(QualificationType.CompleteQualification)}
            value={QualificationType.CompleteQualification}
            checked={(programType.qualificationMask & QualificationType.CompleteQualification) !== 0}
            onChange={handleQualificationMaskChange}
            disabled={true}
          />
        </Col>
      </Row>
      <Row>
        <Col md={12} className="mt-5">
          <Checkbox
            label={getQualificationTypeName(QualificationType.PartialQualification)}
            value={QualificationType.PartialQualification}
            checked={(programType.qualificationMask & QualificationType.PartialQualification) !== 0}
            onChange={handleQualificationMaskChange}
            disabled={true}
          />
        </Col>
      </Row>
      <Row>
        <Col md={12} className="mt-5">
          <Checkbox
            label={getQualificationTypeName(QualificationType.MicroQualification)}
            value={QualificationType.MicroQualification}
            checked={(programType.qualificationMask & QualificationType.MicroQualification) !== 0}
            onChange={handleQualificationMaskChange}
            disabled={true}
          />
        </Col>
      </Row>
      <Row>
        <Col md={4} className="mt-5">
          <DatePicker
            name={nameof<typeof programType>("dateTo")}
            label={t("common.dateTo")}
            value={programType.dateTo}
            onChange={handleChange(setProgramType)}
            isClearable={true}
            minYear={new Date().getFullYear() - 25}
            maxYear={new Date().getFullYear() + 25}
          />
        </Col>
      </Row>
      <Modal
        type="confirm"
        open={openConfirmModal}
        cancelText={t("common.cancel")}
        confirmText={t("common.saveChanges")}
        onClose={handleCancelConfirmModal}
        onConfirm={handleConfirmedConfirmModal}
      >
        <div>
          <Typography component="h1" variant="largeBold">
            {t("common.areYouSureSaveChanges")}
          </Typography>
        </div>
      </Modal>
    </>
  );
}